import { HroasConstraintData as ConstraintData } from '@blisspointmedia/bpm-types/dist/OptimizationConstraints';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import * as Dfns from 'date-fns/fp';

import { Navigate, Route } from 'react-router-dom';
import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';

import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { usePageTitleNav } from '@src/utils/hooks/useNav';

import { awaitJSON, HROASLambdaFetch } from '@src/utils/fetch-utils';

import { useSetError } from '@src/redux/modals';

import { ConstraintSaveBar } from '@Components/ConstraintSaveBar/ConstraintSaveBar';
import { countConstraintChanges } from '@src/utils/constraint-draft-utils';
import { Placement } from '@Components/designConstants';

import { OptimizationHome } from './OptimizationHome';
import { ConstraintView } from './ConstraintView';
import { RunView } from './RunView';

import './Optimizations.scss';
import {
  ROUTES,
  OptimizationsContext,
  ConstraintListItem,
  Optimization,
  RunResultItem,
  ConstraintCandidates,
  CopyConstraintParams,
  DeleteConstraintParams,
  RunOptimizationParams
} from './optimizations-constants';

const PRETTY_DATE_FORMAT = 'yyyy-MM-dd hh:mma';

const ROUTE_VALUES = Object.values(ROUTES);

// Type definitions

export const Optimizations = () => {
  const setError = useSetError();
  const { cid: company } = useCompanyInfo();

  const [optimizations, setOptimizations] = useState<Optimization[]>();
  const [constraintsList, setConstraintsList] = useState<ConstraintListItem[]>();
  const [constraintData, setConstraintData] = useState<ConstraintData>();
  const [lastSavedConstraintData, setLastSavedConstraintData] = useState<ConstraintData>();
  const [constraintCandidates, setConstraintCandidates] = useState<ConstraintCandidates>();
  const [runResult, setRunResult] = useState<RunResultItem[]>();
  const [runId, setRunId] = useState<string>();

  const { path } = usePageTitleNav({
    baseURL: 'hroas/budget-optimizations',
    routes: ROUTE_VALUES
  });
  const currentPathNode = useMemo(() => path[path.length - 1], [path]);
  const isConstraintsPage = useMemo(
    () => currentPathNode?.key === ROUTES.CONSTRAINTS.key,
    [currentPathNode]
  );
  const isRunsPage = useMemo(() => currentPathNode?.key === ROUTES.RUNS.key, [currentPathNode]);

  // Tracks the pending 30s poll timer so the effect below can clear it on unmount / company
  // change — otherwise the recursive chain keeps firing setState on an unmounted component and a
  // company switch spawns a second concurrent chain.
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const listUntilJobsFinish = useCallback(async () => {
    try {
      const optimizationsResult = await HROASLambdaFetch('/optimizations', {
        params: { company }
      });
      const optimizationsData = await awaitJSON<Optimization[]>(optimizationsResult);
      setOptimizations(optimizationsData);

      const stillRunning = optimizationsData.filter(
        row => row.status && row.status.indexOf('SUCC') < 0 && row.status.indexOf('FAIL') < 0
      ).length;

      if (stillRunning > 0) {
        pollTimeoutRef.current = setTimeout(async () => {
          // eslint-disable-next-line react-hooks/immutability -- self-recursive useCallback ref; refactor deferred
          await listUntilJobsFinish();
        }, 30000);
      }
    } catch (e) {
      setError({
        message: `Failed to fetch optimizations for company "${company}". ${e.message}`,
        reportError: e
      });
    }
  }, [company, setError]);

  useEffect(() => {
    if (company) {
      (async () => {
        try {
          const [constraintsResult, candidatesResult] = await Promise.all([
            HROASLambdaFetch('/list_constraints', { params: { company } }),
            HROASLambdaFetch('/constraint_candidates', { params: { company } }),
            listUntilJobsFinish()
          ]);
          const constraintsData = await awaitJSON<ConstraintListItem[]>(constraintsResult);
          const candidatesData = await awaitJSON<ConstraintCandidates>(candidatesResult);
          setConstraintsList(constraintsData);
          setConstraintCandidates(candidatesData);
        } catch (e: any) {
          setError({
            message: `Failed to fetch optimizations for company "${company}". ${e.message}`,
            reportError: e
          });
        }
      })();
    }
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [company, setError, listUntilJobsFinish]);

  useEffect(() => {
    if (company && isConstraintsPage) {
      (async () => {
        if (!currentPathNode?.resolvedPath) {
          return;
        }
        const constraintId = currentPathNode.resolvedPath.replace('constraints/', '');

        const urlParams = new URLSearchParams(window.location.search);
        const isNewConstraint = urlParams.get('new') === 'true';

        if (isNewConstraint) {
          const defaultConstraintData: ConstraintData = {
            id: 0,
            company,
            name: constraintId,
            constraints: {
              total: 0,
              dg_abs_bounds: [],
              dg_share_bounds: [],
              dc_abs_caps: [],
              dc_share_caps: [],
              global: {
                r_mmm_weight: 1.0,
                geo_mmm_weight: 1.0,
                brand_equity_weight: 1.0
              }
            },
            lastuser: '',
            lastmodified: new Date().toISOString()
          };
          setConstraintData(defaultConstraintData);
          setLastSavedConstraintData(defaultConstraintData);
          return;
        }

        try {
          const constraintsResult = await HROASLambdaFetch('/constraints', {
            params: { company, name: constraintId }
          });
          const constraintDataResult = await awaitJSON<ConstraintData>(constraintsResult);

          setConstraintData(constraintDataResult);
          setLastSavedConstraintData(constraintDataResult);
        } catch (e: any) {
          setError({
            message: `Failed to fetch constraint ${constraintId} for company "${company}". ${e.message}`,
            reportError: e
          });
        }
      })();
    }
  }, [company, currentPathNode, setError, isConstraintsPage]);

  useEffect(() => {
    if (company && isRunsPage) {
      (async () => {
        if (!currentPathNode?.resolvedPath) {
          return;
        }
        const runIdFromPath = currentPathNode.resolvedPath.replace('runs/', '');
        setRunId(runIdFromPath);
        try {
          const runResultData = await HROASLambdaFetch('/optimizations', {
            params: { company, runId: runIdFromPath }
          });
          const runResultParsed = await awaitJSON<RunResultItem[]>(runResultData);
          setRunResult(runResultParsed);
        } catch (e: any) {
          setError({
            message: `Failed to fetch run results for run ${runIdFromPath}. ${e.message}`,
            reportError: e
          });
        }
      })();
    }
  }, [company, currentPathNode, setError, isRunsPage]);

  const lastModExperimental = useMemo(() => {
    if (!constraintData || !constraintData.lastmodified) {
      return '';
    }

    try {
      const lastModDate = Dfns.parseISO(constraintData.lastmodified);
      const formattedDate = Dfns.format(PRETTY_DATE_FORMAT)(lastModDate);
      const daysDiff = Dfns.differenceInDays(lastModDate, new Date());
      const daysAgoText = daysDiff === 0 ? 'Today' : `${Math.abs(daysDiff)} days ago`;

      /* eslint-disable react-hooks/error-boundaries -- JSX built inside try/catch to guard the date parse; disabling is the safe choice */
      return (
        <OverlayTrigger
          placement={Placement.BOTTOM}
          overlay={<Tooltip id='constraint-last-modified-tooltip'>{formattedDate}</Tooltip>}
        >
          <span className='last-modified'>
            {`Last modified ${
              constraintData.lastuser ? `by ${constraintData.lastuser.split('@')[0]}` : ''
            } ${daysAgoText}`}
          </span>
        </OverlayTrigger>
      );
      /* eslint-enable react-hooks/error-boundaries */
    } catch {
      console.warn('Invalid date for lastmodified:', constraintData.lastmodified);
      return '';
    }
  }, [constraintData]);

  const [savingConstraint, setSavingConstraint] = useState(false);
  const changeCount =
    constraintData && lastSavedConstraintData
      ? Math.max(
          lastSavedConstraintData.id === 0 ? 1 : 0,
          countConstraintChanges(lastSavedConstraintData.constraints, constraintData.constraints)
        )
      : 0;
  const invalidBudget =
    !Number.isFinite(constraintData?.constraints.total) ||
    (constraintData?.constraints.total ?? 0) < 0;

  const saveConstraint = useCallback(async () => {
    if (!constraintData || !changeCount || invalidBudget || savingConstraint) return;
    setSavingConstraint(true);
    const urlParams = new URLSearchParams(window.location.search);
    const wasNewConstraint = urlParams.get('new') === 'true';

    try {
      const saved = await awaitJSON<ConstraintData>(
        await HROASLambdaFetch('/constraints', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: constraintData
        })
      );
      setConstraintData(saved);
      setLastSavedConstraintData(saved);

      // Refresh constraints list so it appears when navigating back
      const constraintsListResult = await HROASLambdaFetch('/list_constraints', {
        params: { company }
      });
      const constraintsListData = await awaitJSON<ConstraintListItem[]>(constraintsListResult);
      setConstraintsList(constraintsListData);

      // If this was a new constraint, clear the ?new=true parameter
      if (wasNewConstraint) {
        const newUrl = window.location.pathname;
        window.history.replaceState(window.history.state, '', newUrl);
      }
    } catch (e: any) {
      setError({ message: e.message, reportError: e });
    }
    setSavingConstraint(false);
  }, [constraintData, changeCount, invalidBudget, savingConstraint, setError, company]);

  const copyConstraint = useCallback(
    async ({ from, to }: CopyConstraintParams) => {
      try {
        const constraintsResult = await HROASLambdaFetch('/constraints', {
          params: { company, name: from }
        });
        const constraintsData = await awaitJSON<ConstraintData>(constraintsResult);

        await HROASLambdaFetch('/constraints', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: { ...constraintsData, name: to }
        });

        const constraintsListResult = await HROASLambdaFetch('/list_constraints', {
          params: { company }
        });
        const constraintsListData = await awaitJSON<ConstraintListItem[]>(constraintsListResult);
        setConstraintsList(constraintsListData);
      } catch (e: any) {
        setError({ message: e.message, reportError: e });
      }
    },
    [company, setError]
  );

  const deleteConstraint = useCallback(
    async ({ deleteConstraintId }: DeleteConstraintParams) => {
      try {
        await HROASLambdaFetch('/delete_constraints', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: { company, name: deleteConstraintId }
        });

        const constraintsListResult = await HROASLambdaFetch('/list_constraints', {
          params: { company }
        });
        const constraintsListData = await awaitJSON<ConstraintListItem[]>(constraintsListResult);
        setConstraintsList(constraintsListData);
      } catch (e: any) {
        setError({ message: e.message, reportError: e });
      }
    },
    [company, setError]
  );

  const runOptimization = useCallback(
    async ({ name }: RunOptimizationParams) => {
      try {
        await HROASLambdaFetch('/run_batch_optimizer', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: { company, name }
        });

        await listUntilJobsFinish();
      } catch (e: any) {
        setError({ message: e.message, reportError: e });
      }
    },
    [company, setError, listUntilJobsFinish]
  );

  const contextValue = useMemo(
    () => ({
      company,
      optimizations,
      constraintsList,
      constraintData,
      savingConstraint,
      constraintCandidates,
      setConstraintData,
      copyConstraint,
      deleteConstraint,
      runOptimization,
      runResult,
      runId
    }),
    [
      company,
      optimizations,
      constraintsList,
      constraintData,
      savingConstraint,
      constraintCandidates,
      setConstraintData,
      copyConstraint,
      deleteConstraint,
      runOptimization,
      runResult,
      runId
    ]
  );

  return (
    <OptimizationsContext.Provider value={contextValue}>
      {isConstraintsPage && constraintData && (
        <div className='optimization-actions-bar'>
          <ConstraintSaveBar
            changeCount={changeCount}
            saving={savingConstraint}
            disabled={invalidBudget}
            onSave={saveConstraint}
            onDiscard={() => setConstraintData(lastSavedConstraintData)}
          >
            <span>HROAS Optimization: {constraintData.name}</span>
            {lastModExperimental}
          </ConstraintSaveBar>
        </div>
      )}
      <div className='optimization-page'>
        <Routes>
          <Route path={`${ROUTES.RUNS.uri}/:optimizationId`} element={<RunView />} />
          <Route path={`${ROUTES.CONSTRAINTS.uri}/:constraintId`} element={<ConstraintView />} />
          <Route index element={<OptimizationHome />} />
          <Route path='*' element={<Navigate to='.' replace />} />
        </Routes>
      </div>
    </OptimizationsContext.Provider>
  );
};
